import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Receipt,
  Plus,
  Search,
  Edit,
  Trash2,
  DollarSign,
  Calendar,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Filter,
  RotateCcw,
  Copy,
  FileText,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const PAGE_SIZE = 100;

const statusColors = {
  a_vencer: 'bg-blue-500',
  vencida: 'bg-red-500',
  paga: 'bg-green-500',
  cancelada: 'bg-gray-500'
};

const statusLabels = {
  a_vencer: 'A Vencer',
  vencida: 'Vencida',
  paga: 'Paga',
  cancelada: 'Cancelada'
};

export default function InvoicesPage() {
  const { API } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('vencidas_hoje');
  const [search, setSearch] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  // Paginação (feita no servidor: o filtro inteiro, não só o que está na tela)
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalValor, setTotalValor] = useState(0);
  const requestId = useRef(0);

  // Selection
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  
  // Form state
  const [editData, setEditData] = useState({
    vencimento: '',
    valor_original: 0,
    multa: 0,
    observacoes: ''
  });
  
  // Payment form state
  const [paymentData, setPaymentData] = useState({
    data_pagamento: new Date().toISOString().split('T')[0],
    valor_original: 0,
    multa: 0,
    valor_pago: 0,
    usar_valor_diferente: false
  });
  
  const [submitting, setSubmitting] = useState(false);

  // Valor total do filtro inteiro (calculado no servidor, com a mesma regra de antes:
  // pagas/canceladas só somam quando são o filtro; nos demais, só as abertas)
  const calculateFilteredTotal = () => totalValor;

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Mudou o filtro: volta para a página 1 e limpa a seleção
  useEffect(() => {
    setPage(1);
    setSelectedInvoices([]);
    loadStats();
  }, [statusFilter, search, dataInicio, dataFim]);

  // Carrega a página atual (e recarrega ao trocar de página ou de filtro)
  useEffect(() => {
    loadInvoices();
  }, [statusFilter, search, dataInicio, dataFim, page]);

  const loadInvoices = async () => {
    const myRequest = ++requestId.current;
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'todas') params.append('status', statusFilter);
      if (search) params.append('search', search);
      if (dataInicio) params.append('data_inicio', dataInicio);
      if (dataFim) params.append('data_fim', dataFim);
      params.append('limit', PAGE_SIZE);
      params.append('skip', (page - 1) * PAGE_SIZE);

      const response = await axios.get(`${API}/invoices?${params}`, { withCredentials: true });
      if (myRequest !== requestId.current) return; // resposta antiga, já veio outra consulta

      const count = parseInt(response.headers['x-total-count'] ?? response.data.length, 10);
      // Apagou/pagou o último item da página: volta uma página
      if (response.data.length === 0 && page > 1 && count > 0) {
        setPage(page - 1);
        return;
      }
      setInvoices(response.data);
      setTotalCount(count);
      setTotalValor(parseFloat(response.headers['x-total-valor'] ?? 0));
    } catch (error) {
      toast.error('Erro ao carregar faturas');
    } finally {
      if (myRequest === requestId.current) setLoading(false);
    }
  };

  const goToPage = (newPage) => {
    setSelectedInvoices([]); // a seleção vale só para a página mostrada
    setPage(Math.min(Math.max(1, newPage), totalPages));
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/invoices/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.put(`${API}/invoices/${selectedInvoice.invoice_id}`, editData, { withCredentials: true });
      toast.success('Fatura atualizada com sucesso!');
      setShowEditModal(false);
      loadInvoices();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar fatura');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.put(`${API}/invoices/bulk/update`, selectedInvoices, {
        params: editData,
        withCredentials: true
      });
      toast.success(`${selectedInvoices.length} faturas atualizadas!`);
      setShowBulkEditModal(false);
      setSelectedInvoices([]);
      setSelectMode(false);
      loadInvoices();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar faturas');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePay = async () => {
    setSubmitting(true);
    try {
      const valorFinal = paymentData.usar_valor_diferente ? paymentData.valor_pago : (paymentData.valor_original + paymentData.multa);
      
      const response = await axios.put(`${API}/invoices/${selectedInvoice.invoice_id}/pay`, {
        data_pagamento: paymentData.data_pagamento,
        valor_pago: valorFinal
      }, { withCredentials: true });
      
      // Prepare receipt data
      const nextInvoice = await findNextInvoice(selectedInvoice);
      setReceiptData({
        client_name: selectedInvoice.client_name,
        vencimento: selectedInvoice.vencimento,
        valor_pago: valorFinal,
        data_pagamento: paymentData.data_pagamento,
        proxima_vencer: nextInvoice?.vencimento || null,
        vehicle_info: selectedInvoice.vehicle_info
      });
      
      setShowPayModal(false);
      setShowReceiptModal(true);
      toast.success('Pagamento registrado com sucesso!');
      loadInvoices();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar pagamento');
    } finally {
      setSubmitting(false);
    }
  };

  const findNextInvoice = async (currentInvoice) => {
    try {
      const response = await axios.get(`${API}/invoices?contract_id=${currentInvoice.contract_id}&status=a_vencer`, { withCredentials: true });
      const futureInvoices = response.data.filter(inv => inv.vencimento > currentInvoice.vencimento);
      if (futureInvoices.length > 0) {
        return futureInvoices.sort((a, b) => a.vencimento.localeCompare(b.vencimento))[0];
      }
      return null;
    } catch (error) {
      console.error('Error finding next invoice:', error);
      return null;
    }
  };

  const handleRefund = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/invoices/${selectedInvoice.invoice_id}/refund`, {}, { withCredentials: true });
      toast.success('Estorno realizado com sucesso!');
      setShowRefundDialog(false);
      loadInvoices();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao estornar pagamento');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (invoice) => {
    try {
      await axios.put(`${API}/invoices/${invoice.invoice_id}/cancel`, {}, { withCredentials: true });
      toast.success('Fatura cancelada!');
      loadInvoices();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar fatura');
    }
  };

  const openEditModal = (invoice) => {
    if (invoice.status === 'paga' || invoice.status === 'cancelada') {
      toast.error('Faturas pagas ou canceladas não podem ser editadas');
      return;
    }
    setSelectedInvoice(invoice);
    setEditData({
      vencimento: invoice.vencimento,
      valor_original: invoice.valor_original,
      multa: invoice.multa,
      observacoes: invoice.observacoes || ''
    });
    setShowEditModal(true);
  };

  const openPayModal = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentData({
      data_pagamento: new Date().toISOString().split('T')[0],
      valor_original: invoice.valor_original,
      multa: invoice.multa || 0,
      valor_pago: invoice.valor_atualizado || invoice.valor_original,
      usar_valor_diferente: false
    });
    setShowPayModal(true);
  };

  // Calcular multa automaticamente quando a data de pagamento mudar
  const calculatePenalty = async (dataPagamento, invoice) => {
    if (!dataPagamento || !invoice) return 0;
    
    const vencimento = new Date(invoice.vencimento);
    const pagamento = new Date(dataPagamento);
    
    // Se pagamento está dentro do prazo ou antes, sem multa
    if (pagamento <= vencimento) {
      return 0;
    }
    
    // Calcular dias de atraso
    const diasAtraso = Math.floor((pagamento - vencimento) / (1000 * 60 * 60 * 24));
    
    // Buscar multa diária do contrato
    try {
      const response = await axios.get(`${API}/contracts/${invoice.contract_id}`, { withCredentials: true });
      const multaDiaria = response.data.multa_diaria || 0;
      return multaDiaria * diasAtraso;
    } catch (error) {
      console.error('Erro ao buscar multa do contrato:', error);
      return 0;
    }
  };

  // Handler para mudança de data no modal de pagamento
  const handlePaymentDateChange = async (newDate) => {
    const novaMulta = await calculatePenalty(newDate, selectedInvoice);
    const valorAtualizado = paymentData.valor_original + novaMulta;
    
    setPaymentData({
      ...paymentData,
      data_pagamento: newDate,
      multa: novaMulta,
      valor_pago: valorAtualizado
    });
  };

  const openReceiptModal = async (invoice) => {
    setSelectedInvoice(invoice);
    const nextInvoice = await findNextInvoice(invoice);
    setReceiptData({
      client_name: invoice.client_name,
      vencimento: invoice.vencimento,
      valor_pago: invoice.valor_pago || invoice.valor_atualizado || invoice.valor_original,
      data_pagamento: invoice.data_pagamento || invoice.vencimento,
      proxima_vencer: nextInvoice?.vencimento || null,
      vehicle_info: invoice.vehicle_info
    });
    setShowReceiptModal(true);
  };

  const openRefundDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setShowRefundDialog(true);
  };

  const openDeleteDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDeleteDialog(true);
  };

  const handleDeleteInvoice = async () => {
    if (!selectedInvoice) return;
    
    try {
      await axios.delete(`${API}/invoices/${selectedInvoice.invoice_id}`, { withCredentials: true });
      toast.success('Parcela excluída com sucesso!');
      setShowDeleteDialog(false);
      setSelectedInvoice(null);
      loadInvoices();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir parcela');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedInvoices.length === 0) return;
    
    setSubmitting(true);
    try {
      // Delete each selected invoice
      for (const invoiceId of selectedInvoices) {
        await axios.delete(`${API}/invoices/${invoiceId}`, { withCredentials: true });
      }
      toast.success(`${selectedInvoices.length} parcela(s) excluída(s) com sucesso!`);
      setShowBulkDeleteDialog(false);
      setSelectedInvoices([]);
      setSelectMode(false);
      loadInvoices();
      loadStats();
    } catch (error) {
      toast.error('Erro ao excluir parcelas');
    } finally {
      setSubmitting(false);
    }
  };

  const openBulkEditModal = () => {
    const editableInvoices = selectedInvoices.filter(id => {
      const inv = invoices.find(i => i.invoice_id === id);
      return inv && inv.status !== 'paga' && inv.status !== 'cancelada';
    });
    
    if (editableInvoices.length === 0) {
      toast.error('Faturas pagas ou canceladas não podem ser editadas');
      return;
    }
    
    if (editableInvoices.length !== selectedInvoices.length) {
      toast.warning('Algumas faturas pagas ou canceladas foram removidas da seleção');
      setSelectedInvoices(editableInvoices);
    }
    
    setEditData({
      vencimento: '',
      valor_original: 0,
      multa: 0,
      observacoes: ''
    });
    setShowBulkEditModal(true);
  };

  const toggleSelectInvoice = (invoiceId) => {
    if (selectedInvoices.includes(invoiceId)) {
      setSelectedInvoices(selectedInvoices.filter(id => id !== invoiceId));
    } else {
      setSelectedInvoices([...selectedInvoices, invoiceId]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedInvoices.length === invoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoices.map(i => i.invoice_id));
    }
  };

  const clearFilters = () => {
    setSearch('');
    setDataInicio('');
    setDataFim('');
    setStatusFilter('todas');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const copyReceipt = () => {
    if (!receiptData) return;
    
    const text = `Recibo de Pagamento de Fatura!

Muito obrigado Senhor(a) ${receiptData.client_name}, acabamos de receber o seu pagamento referente à locação do veículo ${receiptData.vehicle_info || '-'} do período ${formatDate(receiptData.vencimento)} a ${receiptData.proxima_vencer ? formatDate(receiptData.proxima_vencer) : '-'}.

Valor Pago: ${formatCurrency(receiptData.valor_pago)}

Agradecemos a preferência!`;

    navigator.clipboard.writeText(text).then(() => {
      toast.success('Recibo copiado para a área de transferência!');
    }).catch(() => {
      toast.error('Erro ao copiar recibo');
    });
  };

  // Check if invoice is severely overdue (3+ days)
  const isSeverelyOverdue = (invoice) => {
    return invoice.status === 'vencida' && invoice.dias_atraso >= 3;
  };

  // Calculate total to receive in payment modal
  const valorTotalReceber = paymentData.usar_valor_diferente 
    ? paymentData.valor_pago 
    : (paymentData.valor_original + paymentData.multa);

  const filteredInvoices = invoices;

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header with Total */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope]">Faturamento</h1>
          <p className="text-gray-500 text-sm">Gerencie as faturas e cobranças</p>
        </div>
        <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <CardContent className="p-4">
            <p className="text-sm text-blue-100">
              {statusFilter === 'vencidas_hoje' ? 'Valor Vencidas/Hoje' : 
               statusFilter === 'vencida' ? 'Valor Vencidas' :
               statusFilter === 'a_vencer' ? 'Valor A Vencer' :
               statusFilter === 'paga' ? 'Valor Pagas' :
               statusFilter === 'cancelada' ? 'Valor Canceladas' :
               'Valor Total a Receber'}
            </p>
            <p className="text-2xl font-bold">{formatCurrency(calculateFilteredTotal())}</p>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button 
          variant={selectMode ? "default" : "outline"} 
          onClick={() => {
            setSelectMode(!selectMode);
            setSelectedInvoices([]);
          }}
          data-testid="select-mode-button"
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          {selectMode ? 'Cancelar Seleção' : 'Selecionar'}
        </Button>
        {selectMode && selectedInvoices.length > 0 && (
          <>
            <Button onClick={openBulkEditModal} data-testid="bulk-edit-button">
              <Edit className="w-4 h-4 mr-2" />
              Editar Múltiplas ({selectedInvoices.length})
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => setShowBulkDeleteDialog(true)} 
              data-testid="bulk-delete-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir Selecionadas ({selectedInvoices.length})
            </Button>
          </>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar ID, contrato, cliente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-invoices"
              />
            </div>
            <div>
              <Input
                type="date"
                placeholder="Data início"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                data-testid="filter-data-inicio"
              />
            </div>
            <div>
              <Input
                type="date"
                placeholder="Data fim"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                data-testid="filter-data-fim"
              />
            </div>
            <Button variant="outline" onClick={clearFilters}>
              <Filter className="w-4 h-4 mr-2" />
              Limpar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Status Tabs */}
      <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="vencidas_hoje" data-testid="tab-vencidas-hoje" className="data-[state=active]:bg-red-100 data-[state=active]:text-red-700">
            Vencidas/Hoje ({stats.vencidas_hoje || 0})
          </TabsTrigger>
          <TabsTrigger value="a_vencer" data-testid="tab-a-vencer">
            A Vencer ({stats.a_vencer || 0})
          </TabsTrigger>
          <TabsTrigger value="paga" data-testid="tab-pagas">
            Pagas ({stats.pagas || 0})
          </TabsTrigger>
          <TabsTrigger value="cancelada" data-testid="tab-canceladas">
            Canceladas ({stats.canceladas || 0})
          </TabsTrigger>
          <TabsTrigger value="todas" data-testid="tab-todas">
            Todas ({stats.total || 0})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Receipt className="w-12 h-12 mb-2 opacity-50" />
              <p>Nenhuma fatura encontrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {selectMode && (
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedInvoices.length === filteredInvoices.length}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                    )}
                    <TableHead>ID Veículo</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Valor Original</TableHead>
                    <TableHead>Valor Atualizado</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => {
                    const severelyOverdue = isSeverelyOverdue(invoice);
                    
                    return (
                      <TableRow 
                        key={invoice.invoice_id} 
                        data-testid={`invoice-row-${invoice.invoice_id}`}
                        className={severelyOverdue ? 'bg-red-100 hover:bg-red-200 border-l-4 border-l-red-500' : ''}
                      >
                        {selectMode && (
                          <TableCell>
                            <Checkbox
                              checked={selectedInvoices.includes(invoice.invoice_id)}
                              onCheckedChange={() => toggleSelectInvoice(invoice.invoice_id)}
                              disabled={invoice.status === 'paga' || invoice.status === 'cancelada'}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <div>
                            <p className={`font-mono text-sm font-bold ${severelyOverdue ? 'text-red-700' : ''}`}>{invoice.vehicle_id}</p>
                            <p className="text-xs text-gray-500">{invoice.vehicle_info}</p>
                            {invoice.nome_parcela && (
                              <p className="text-xs text-blue-600 font-medium mt-1">
                                📌 {invoice.nome_parcela}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className={`font-medium ${severelyOverdue ? 'text-red-700' : ''}`}>{invoice.client_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Calendar className={`w-3 h-3 ${severelyOverdue ? 'text-red-500' : 'text-gray-400'}`} />
                            <span className={severelyOverdue ? 'text-red-700 font-medium' : ''}>{formatDate(invoice.vencimento)}</span>
                          </div>
                          {invoice.dias_atraso > 0 && (
                            <p className={`text-xs ${severelyOverdue ? 'text-red-700 font-bold' : 'text-red-500'}`}>
                              {severelyOverdue && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                              {invoice.dias_atraso} dias em atraso
                            </p>
                          )}
                        </TableCell>
                        <TableCell className={severelyOverdue ? 'text-red-700' : ''}>{formatCurrency(invoice.valor_original)}</TableCell>
                        <TableCell className={`font-semibold ${severelyOverdue ? 'text-red-700' : ''}`}>
                          {formatCurrency(invoice.valor_atualizado)}
                          {invoice.multa > 0 && (
                            <p className={`text-xs ${severelyOverdue ? 'text-red-700' : 'text-red-500'}`}>+ {formatCurrency(invoice.multa)} multa</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusColors[invoice.status]} text-white ${severelyOverdue ? 'animate-pulse' : ''}`}>
                            {statusLabels[invoice.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {invoice.status === 'paga' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openReceiptModal(invoice)}
                                  className="text-blue-600 hover:text-blue-700"
                                  title="Ver Recibo"
                                  data-testid={`receipt-invoice-${invoice.invoice_id}`}
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openRefundDialog(invoice)}
                                  className="text-amber-600 hover:text-amber-700"
                                  title="Estornar Pagamento"
                                  data-testid={`refund-invoice-${invoice.invoice_id}`}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {invoice.status !== 'paga' && invoice.status !== 'cancelada' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openPayModal(invoice)}
                                  className="text-green-600 hover:text-green-700"
                                  title="Registrar Pagamento"
                                  data-testid={`pay-invoice-${invoice.invoice_id}`}
                                >
                                  <DollarSign className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditModal(invoice)}
                                  title="Editar"
                                  data-testid={`edit-invoice-${invoice.invoice_id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleCancel(invoice)}
                                  className="text-amber-600 hover:text-amber-700"
                                  title="Cancelar"
                                  data-testid={`cancel-invoice-${invoice.invoice_id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openDeleteDialog(invoice)}
                                  className="text-red-600 hover:text-red-700"
                                  title="Excluir Parcela"
                                  data-testid={`delete-invoice-${invoice.invoice_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Paginação */}
          {totalCount > 0 && (
            <div
              className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t px-4 py-3 text-sm text-gray-600"
              data-testid="invoices-pagination"
            >
              <span>
                Mostrando {((page - 1) * PAGE_SIZE + 1).toLocaleString('pt-BR')}–
                {Math.min(page * PAGE_SIZE, totalCount).toLocaleString('pt-BR')} de{' '}
                {totalCount.toLocaleString('pt-BR')} {totalCount === 1 ? 'fatura' : 'faturas'}
              </span>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(page - 1)}
                    disabled={page <= 1}
                    data-testid="invoices-prev-page"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Anterior
                  </Button>
                  <span className="px-2 tabular-nums">
                    Página {page.toLocaleString('pt-BR')} de {totalPages.toLocaleString('pt-BR')}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(page + 1)}
                    disabled={page >= totalPages}
                    data-testid="invoices-next-page"
                  >
                    Próxima
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Fatura</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div>
              <Label htmlFor="vencimento">Data de Vencimento</Label>
              <Input
                id="vencimento"
                type="date"
                value={editData.vencimento}
                onChange={(e) => setEditData({ ...editData, vencimento: e.target.value })}
                data-testid="edit-vencimento"
              />
            </div>
            
            <div>
              <Label htmlFor="valor_original">Valor Original (R$)</Label>
              <Input
                id="valor_original"
                type="number"
                step="0.01"
                min="0"
                value={editData.valor_original}
                onChange={(e) => setEditData({ ...editData, valor_original: parseFloat(e.target.value) || 0 })}
                data-testid="edit-valor"
              />
            </div>
            
            <div>
              <Label htmlFor="multa">Multa (R$)</Label>
              <Input
                id="multa"
                type="number"
                step="0.01"
                min="0"
                value={editData.multa}
                onChange={(e) => setEditData({ ...editData, multa: parseFloat(e.target.value) || 0 })}
                data-testid="edit-multa"
              />
            </div>
            
            <div>
              <Label htmlFor="observacoes">Observações</Label>
              <Input
                id="observacoes"
                value={editData.observacoes}
                onChange={(e) => setEditData({ ...editData, observacoes: e.target.value })}
                data-testid="edit-obs"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-invoice-button">
                {submitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Modal */}
      <Dialog open={showBulkEditModal} onOpenChange={setShowBulkEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar {selectedInvoices.length} Faturas</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBulkEdit} className="space-y-4">
            <p className="text-sm text-gray-500">
              Preencha apenas os campos que deseja alterar. Campos vazios serão mantidos.
            </p>
            
            <div>
              <Label htmlFor="bulk_vencimento">Nova Data de Vencimento</Label>
              <Input
                id="bulk_vencimento"
                type="date"
                value={editData.vencimento}
                onChange={(e) => setEditData({ ...editData, vencimento: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="bulk_valor">Novo Valor (R$)</Label>
              <Input
                id="bulk_valor"
                type="number"
                step="0.01"
                min="0"
                value={editData.valor_original || ''}
                onChange={(e) => setEditData({ ...editData, valor_original: parseFloat(e.target.value) || 0 })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowBulkEditModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-bulk-button">
                {submitting ? 'Salvando...' : 'Aplicar a Todas'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* NEW: Payment Modal with detailed form */}
      <Dialog open={showPayModal} onOpenChange={setShowPayModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar Pagamento</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="data_pagamento">Data do Pagamento</Label>
                <Input
                  id="data_pagamento"
                  type="date"
                  value={paymentData.data_pagamento}
                  onChange={(e) => handlePaymentDateChange(e.target.value)}
                  data-testid="payment-date"
                />
              </div>
              
              <div>
                <Label htmlFor="valor_original_pay">Valor Original</Label>
                <Input
                  id="valor_original_pay"
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentData.valor_original}
                  onChange={(e) => setPaymentData({ ...paymentData, valor_original: parseFloat(e.target.value) || 0 })}
                  data-testid="payment-valor-original"
                />
              </div>
              
              <div>
                <Label htmlFor="multa_pay">Multa por Atraso</Label>
                <Input
                  id="multa_pay"
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentData.multa}
                  onChange={(e) => setPaymentData({ ...paymentData, multa: parseFloat(e.target.value) || 0 })}
                  data-testid="payment-multa"
                />
              </div>
              
              {/* Total to Receive */}
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Valor Total a Receber:</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(valorTotalReceber)}</p>
              </div>
              
              {/* Option to use different value */}
              <div className="flex items-center gap-2 pt-2 border-t">
                <Checkbox
                  id="usar_valor_diferente"
                  checked={paymentData.usar_valor_diferente}
                  onCheckedChange={(checked) => setPaymentData({ ...paymentData, usar_valor_diferente: checked })}
                  data-testid="use-different-value-check"
                />
                <Label htmlFor="usar_valor_diferente" className="font-normal cursor-pointer">
                  Usar valor diferente do calculado
                </Label>
              </div>
              
              {paymentData.usar_valor_diferente && (
                <div>
                  <Label htmlFor="valor_pago">Valor a Pagar</Label>
                  <Input
                    id="valor_pago"
                    type="number"
                    step="0.01"
                    min="0"
                    value={paymentData.valor_pago}
                    onChange={(e) => setPaymentData({ ...paymentData, valor_pago: parseFloat(e.target.value) || 0 })}
                    data-testid="payment-custom-value"
                  />
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowPayModal(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handlePay} 
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="confirm-pay-button"
                >
                  {submitting ? 'Processando...' : 'Confirmar Pagamento'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NEW: Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Recibo de Pagamento de Fatura!</DialogTitle>
          </DialogHeader>
          {receiptData && (
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-gray-700 leading-relaxed">
                  Muito obrigado Senhor(a) <strong>{receiptData.client_name}</strong>, acabamos de receber o seu pagamento referente à locação do veículo <strong>{receiptData.vehicle_info || '-'}</strong> do período <strong>{formatDate(receiptData.vencimento)}</strong> a <strong>{receiptData.proxima_vencer ? formatDate(receiptData.proxima_vencer) : '-'}</strong>.
                </p>
                <p className="mt-3 text-lg font-semibold text-green-700">
                  Valor Pago: {formatCurrency(receiptData.valor_pago)}
                </p>
                <p className="mt-4 text-center text-lg">
                  Agradecemos a preferência!
                </p>
              </div>

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setShowReceiptModal(false)}>
                  Fechar
                </Button>
                <Button 
                  onClick={copyReceipt}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="copy-receipt-button"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Recibo
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <AlertDialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Estornar Pagamento</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja estornar o pagamento da fatura do veículo {selectedInvoice?.vehicle_id}? 
              A fatura voltará para o status anterior (vencida ou a vencer).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRefund}
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-refund-button"
            >
              {submitting ? 'Processando...' : 'Confirmar Estorno'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Single Invoice Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Excluir Parcela</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta parcela? Esta ação não pode ser desfeita.
              {selectedInvoice && (
                <div className="mt-2 p-2 bg-red-50 rounded text-sm">
                  <p><strong>Cliente:</strong> {selectedInvoice.client_name}</p>
                  <p><strong>Vencimento:</strong> {selectedInvoice.vencimento}</p>
                  <p><strong>Valor:</strong> {formatCurrency(selectedInvoice.valor_original)}</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteInvoice}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir Parcela
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Excluir Múltiplas Parcelas</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir <strong>{selectedInvoices.length}</strong> parcela(s) selecionada(s)? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {submitting ? 'Excluindo...' : `Excluir ${selectedInvoices.length} Parcela(s)`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
